import React, { useState } from "react";
import MemberAvatarStack, { TeamMemberBasic } from "./MemberAvatarStack";
import { useNavigate } from "react-router-dom";

export interface CoachTeam {
  id: number;
  name: string;
  teamLead?: { name: string };
  members: TeamMemberBasic[];
  completionPercentage: number;
}

const getProgressColor = (pct: number) => {
  if (pct >= 100) return "#10b981";
  if (pct >= 60) return "#0067ff";
  if (pct >= 30) return "#f59e0b";
  return "#ef4444";
};

const TeamCard: React.FC<{ team: CoachTeam }> = ({ team }) => {
  const navigate = useNavigate();
  const [membersOpen, setMembersOpen] = useState(false);
  const progressColor = getProgressColor(team.completionPercentage);

  console.log(team.members);

  return (
    <div
      className="bg-white rounded-2xl flex flex-col transition-all duration-200 hover:shadow-md overflow-visible"
      style={{
        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
        border: "1px solid #eef0f8",
      }}
    >
      {/* ── Header ── */}
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-[15px] font-bold text-[#26295e] leading-snug font-primary flex-1 min-w-0">
            {team.name}
          </h3>

          {/* Members count dropdown */}
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setMembersOpen((v) => !v)}
              className="flex items-center gap-1 border-0 cursor-pointer bg-transparent p-0 group"
            >
              <span className="text-[12px] font-medium text-[#9ba5b4] group-hover:text-[#5d4cc6] transition-colors font-primary">
                {team.members.length} members
              </span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9ba5b4"
                strokeWidth="2.5"
                className={`transition-transform duration-200 ${membersOpen ? "rotate-180" : ""}`}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {membersOpen && (
              <div
                className="absolute top-full right-0 mt-2 bg-white rounded-xl z-30 w-[200px] overflow-hidden"
                style={{
                  boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
                  border: "1px solid #eef0f8",
                }}
              >
                <p className="px-3 pt-3 pb-1.5 text-[10px] font-bold text-[#9ba5b4] uppercase tracking-wider font-primary">
                  Team Members
                </p>
                <div className="max-h-[180px] overflow-y-auto pb-2">
                  {team.members.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-[#9ba5b4] font-primary">
                      No members yet
                    </p>
                  ) : (
                    team.members.map((m) => (
                      <div
                        key={m.id}
                        className={`flex items-center gap-2.5 px-3 py-2 transition-colors ${
                          m.completed_per === 100
                            ? "hover:bg-[#f8f9ff] cursor-pointer"
                            : "opacity-50 cursor-not-allowed"
                        }`}
                        onClick={() => {
                          if (m.completed_per !== 100) return; // ✅ block if not completed
                          navigate("/individual-results", {
                            state: { user: m },
                          });
                        }}
                      >
                        <div className="w-6 h-6 rounded-full bg-[#e8eaf6] flex items-center justify-center text-[9px] font-bold text-[#5d4cc6] flex-shrink-0 overflow-hidden">
                          {m.image ? (
                            <img
                              src={m.image}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            `${m.first_name[0]}${m.last_name[0]}`
                          )}
                        </div>
                        <span className="text-[12px] text-[#26295e] font-medium font-primary truncate flex-1">
                          {m.first_name} {m.last_name}
                        </span>
                        {/* ✅ Show completion status */}
                        {m.completed_per === 100 ? (
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="2.5"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          <span className="text-[9px] text-[#9ba5b4] font-primary flex-shrink-0">
                            Pending
                          </span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Team lead */}
        {team.teamLead && (
          <p className="text-[12px] text-[#9ba5b4] font-primary">
            Lead:{" "}
            <span className="text-[#26295e] font-semibold">
              {team.teamLead.name}
            </span>
          </p>
        )}
      </div>

      {/* ── Divider ── */}
      <div className="h-px bg-[#f0f3fa] mx-5" />

      {/* ── Avatars + % ── */}
      <div className="px-5 py-4 flex items-center justify-between gap-3">
        <MemberAvatarStack
          members={team.members}
          maxVisible={3}
          size="w-12 h-12"
        />

        <div className="flex items-center gap-3 flex-shrink-0">
          <span
            className="text-[14px] font-bold font-primary"
            style={{ color: progressColor }}
          >
            {team.completionPercentage}%
          </span>
        </div>
      </div>

      {/* ── Progress bar ── */}
      <div className="px-5 pb-1">
        <div className="h-[5px] bg-[#f0f3fa] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.min(team.completionPercentage, 100)}%`,
              background: progressColor,
            }}
          />
        </div>
      </div>

      {/* ── CTA ── */}
      {/* ── CTA ── */}
      <div className="px-5 pt-3 pb-5">
        {team.completionPercentage === 0 ? (
          // No results yet — informational state
          <div
            className="w-full rounded-xl py-3 flex items-center justify-center gap-2"
            style={{ background: "#fef9ec", border: "1px dashed #f59e0b" }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#f59e0b"
              strokeWidth="2.5"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span
              className="text-[11px] font-semibold font-primary"
              style={{ color: "#d97706" }}
            >
              Awaiting survey completion
            </span>
          </div>
        ) : (
          <button
            onClick={() =>
              navigate("/individual-results", {
                state: { teamId: team.id, teamName: team.name },
              })
            }
            className="w-full text-[12px] font-semibold rounded-xl py-3 border-0 cursor-pointer transition-all duration-200 font-primary text-[#26295e] bg-[#f0f3fa] hover:bg-blue hover:text-white"
          >
            View Team Results
          </button>
        )}
      </div>
    </div>
  );
};

export default TeamCard;
